// Live study panel: while an AutoResearch session (or any candidate run) is
// in flight, show the study happening. Two surfaces stack here:
//   - the currently-integrating run's live waveform filling in against its
//     ghost, drawn by LiveRunChart from the waveform_live stream;
//   - the R0 reference + best-so-far + latest candidate waveforms overlaid
//     once they complete (MultiRunOverlayChart), refreshed as each run lands
//     (the parent TestDetailView polls at 2 s while work is in flight, so
//     `test.runs` is live).
// Plus the track-position strip so the engineer can see WHERE on the lap the
// traces differ. Renders nothing when the study is idle; the existing
// Results/Comparison panes own the post-study view. The live surface is
// advisory and never mixes into the completed-run overlay.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { StudyEvaluation } from "../api/evaluation";
import type { LiveGhost } from "../api/liveWaveform";
import { fetchWaveformJson, type WaveformJson } from "../api/runs";
import { cancelRun, type Run, type Test } from "../api/tests";
import { LiveRunChart } from "./LiveRunChart";
import {
  MultiRunOverlayChart, type SelectedRunWaveform,
} from "./MultiRunOverlayChart";
import type { R0RecordedReference } from "./multiRunOverlayAdapter";
import { TrackPositionStrip } from "./TrackPositionStrip";
import { useLiveWaveform } from "./useLiveWaveform";

const M_TO_MM = 1000;

export type LiveStudyPanelProps = {
  test: Test;
  evaluation: StudyEvaluation | null;
};

function fmtDelta(delta: number | null | undefined): string {
  if (delta == null) return "";
  const pct = delta * 100;
  return ` ${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`;
}

export function LiveStudyPanel({ test, evaluation }: LiveStudyPanelProps) {
  const runs = test.runs ?? [];
  const inFlight = runs.filter(
    (r) => r.status === "pending" || r.status === "running",
  );
  const complete = runs.filter((r) => r.status === "complete");
  const r0 = complete.find((r) => r.run_number === 0) ?? null;
  // The run currently being integrated (at most one). It owns the live trace.
  const running = inFlight.find((r) => r.status === "running") ?? null;

  // Best complete candidate by the study objective (evaluation rollup;
  // baseline-relative delta, direction-aware). Undefined without an
  // objective or before any candidate completes.
  const direction = evaluation?.objective?.direction ?? "min";
  const best = useMemo(() => {
    if (!evaluation) return null;
    let pick: { run: Run; delta: number } | null = null;
    for (const r of complete) {
      if (r.run_number === 0) continue;
      const ev = evaluation.runs[String(r.id)];
      const delta = ev?.objective_delta_vs_reference;
      if (delta == null) continue;
      const better =
        pick === null ||
        (direction === "max" ? delta > pick.delta : delta < pick.delta);
      if (better) pick = { run: r, delta };
    }
    return pick;
  }, [evaluation, complete, direction]);

  const latest = useMemo(() => {
    const candidates = complete.filter((r) => r.run_number > 0);
    if (candidates.length === 0) return null;
    return candidates.reduce(
      (m, r) => (r.run_number > m.run_number ? r : m), candidates[0],
    );
  }, [complete]);

  // The runs the overlay shows, in draw order: R0 first (thick base
  // trace), then best, then latest, deduped.
  const overlayRuns = useMemo(() => {
    const seen = new Set<number>();
    const out: Array<{ run: Run; role: string; delta: number | null }> = [];
    const push = (run: Run | null, role: string, delta: number | null) => {
      if (!run || seen.has(run.id)) return;
      seen.add(run.id);
      out.push({ run, role, delta });
    };
    push(r0, "reference", null);
    push(best?.run ?? null, "best", best?.delta ?? null);
    push(
      latest, "latest",
      latest && evaluation
        ? evaluation.runs[String(latest.id)]?.objective_delta_vs_reference
          ?? null
        : null,
    );
    return out;
  }, [r0, best, latest, evaluation]);

  const overlayIdsKey = overlayRuns.map((o) => o.run.id).join(",");

  const [waveforms, setWaveforms] = useState<Map<number, WaveformJson>>(
    new Map(),
  );
  const ctrlRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (overlayRuns.length === 0) return;
    ctrlRef.current?.abort();
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    (async () => {
      try {
        const fetched = await Promise.all(
          overlayRuns.map((o) =>
            fetchWaveformJson(o.run.id, {
              maxPoints: 1000, signal: ctrl.signal,
            }).then((w) => [o.run.id, w] as const),
          ),
        );
        if (ctrl.signal.aborted) return;
        setWaveforms(new Map(fetched));
      } catch {
        // Best-effort: a failed waveform fetch (run raced a delete, artifact
        // not yet on disk) just skips this refresh; the next poll retries.
      }
    })();
    return () => ctrl.abort();
    // overlayIdsKey is the identity of the fetch set; refetch only when
    // membership changes (a new run completed / became best).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayIdsKey]);

  // Live waveform of the currently-integrating run. Passing null (no running
  // run) disables the poll loop.
  const live = useLiveWaveform(running?.id ?? null);

  // Per-run "cancelling" latch so the Abort button disables after a click.
  const [cancelling, setCancelling] = useState<Set<number>>(new Set());
  const abortRun = useCallback(async (id: number) => {
    setCancelling((prev) => new Set(prev).add(id));
    try {
      await cancelRun(id);
    } catch {
      // Best-effort: the run may already have finished; the parent poll will
      // reflect the real status. Re-enable so the engineer can retry.
      setCancelling((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, []);

  // Ghost for the live chart: the recorded truth (7-post) when the stream
  // carries it, else the R0 baseline predicted waveform the panel already
  // fetched (replay studies have no recorded truth). No ghost when R0 itself
  // is the run being integrated.
  const r0BaselineGhost = useMemo<LiveGhost | null>(() => {
    if (!r0 || (running != null && r0.id === running.id)) return null;
    const w = waveforms.get(r0.id);
    if (!w) return null;
    return {
      timeS: w.time_s,
      channels: {
        lf: w.predicted.lf.map((v) => v * M_TO_MM),
        rf: w.predicted.rf.map((v) => v * M_TO_MM),
        lr: w.predicted.lr.map((v) => v * M_TO_MM),
        rr: w.predicted.rr.map((v) => v * M_TO_MM),
      },
    };
  }, [r0, running, waveforms]);
  const liveGhost = live.ghost ?? r0BaselineGhost;

  const showLive =
    running != null && live.durationS != null && live.status != null;
  const renderOverlay = inFlight.length > 0 && overlayRuns.length > 0;
  // Hide when idle or nothing to show yet.
  if (!showLive && !renderOverlay) return null;

  const loaded = overlayRuns.filter((o) => waveforms.has(o.run.id));
  const first = loaded[0] ? waveforms.get(loaded[0].run.id)! : null;

  const selectedRuns: SelectedRunWaveform[] = loaded
    .filter((o) => {
      const w = waveforms.get(o.run.id)!;
      return first != null && w.time_s.length === first.time_s.length;
    })
    .map((o) => {
      const roleLabel =
        o.role === "reference"
          ? "R0 · reference"
          : `R${o.run.run_number} · ${o.role}${fmtDelta(o.delta)}`;
      return {
        id: o.run.id,
        runNumber: o.run.run_number,
        intentLabel: roleLabel,
        predicted: waveforms.get(o.run.id)!.predicted,
      };
    });

  const r0Waveform = r0 ? waveforms.get(r0.id) : undefined;
  const r0Recorded: R0RecordedReference | null =
    r0Waveform?.recorded &&
    r0Waveform.recorded.lf != null &&
    r0Waveform.recorded.rf != null &&
    r0Waveform.recorded.lr != null &&
    r0Waveform.recorded.rr != null
      ? {
          time_s: r0Waveform.time_s,
          recorded: {
            lf: r0Waveform.recorded.lf,
            rf: r0Waveform.recorded.rf,
            lr: r0Waveform.recorded.lr,
            rr: r0Waveform.recorded.rr,
          },
        }
      : null;

  return (
    <section className="card live-study-panel" data-testid="live-study-panel">
      <h4 style={{ marginTop: 0, display: "flex", alignItems: "baseline", gap: "10px" }}>
        Live study
        <span className="muted" style={{ fontSize: "12px", fontWeight: 400 }}>
          {inFlight.length} run(s) in flight
          {running
            ? ` · R${running.run_number}${running.status_detail ? `: ${running.status_detail}` : " running"}`
            : " (queued)"}
        </span>
        {running && (
          <button
            type="button"
            className="btn live-abort-btn"
            data-testid="live-abort-button"
            disabled={cancelling.has(running.id)}
            onClick={() => void abortRun(running.id)}
            title="Cancel this run mid-solve"
          >
            {cancelling.has(running.id) ? "cancelling…" : "Abort run"}
          </button>
        )}
      </h4>
      {showLive && running && (
        <LiveRunChart
          runId={running.id}
          runNumber={running.run_number}
          durationS={live.durationS!}
          timeS={live.timeS}
          channels={live.channels}
          ghost={liveGhost}
          sampleCount={live.sampleCount}
          simTimeS={live.simTimeS}
          status={live.status!}
          liveR2={live.liveR2}
        />
      )}
      {renderOverlay &&
        (selectedRuns.length > 0 && first ? (
          <MultiRunOverlayChart
            timeS={first.time_s}
            selectedRuns={selectedRuns}
            r0RecordedReference={r0Recorded}
          />
        ) : (
          <p className="muted" style={{ fontSize: "12px" }}>
            Waiting for the first completed run’s waveform…
          </p>
        ))}
      <TrackPositionStrip testId={test.id} />
    </section>
  );
}
